import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import type { RoutingFileApi } from '../routingApi';
import { fileAll } from '../utils/fileAttachments';
import { fileTooLarge } from '../routingForm';
import { RoutingFilesInput } from './RoutingFilesInput';

/**
 * Files more soft copies or scans against a document that already exists.
 *
 * **No Autogenerate, and deliberately** — the one field is a file whose type the server verifies
 * by its magic bytes, and no textual format can be written honestly into it. The rule's own
 * exemption: the prop is omitted, so no button renders.
 *
 * Each file is its own request; one that fails is named and the rest still land.
 */
export function AttachFilesModal({
  open,
  recordId,
  trackingNumber,
  api,
  onClose,
  onSaved,
}: {
  open: boolean;
  recordId: string;
  trackingNumber: string;
  /** Only `attach` is called — a dispatch's files and a forward's are filed through the same form. */
  api: Pick<RoutingFileApi, 'attach'>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failures, setFailures] = useState<{ name: string; reason: string }[]>([]);

  const canSave = files.length > 0 && !files.some(fileTooLarge) && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setFailures([]);
    setProgress(0);

    const failed = await fileAll(api, recordId, files, setProgress);
    const filed = files.length - failed.length;

    setSaving(false);

    if (filed > 0) {
      toast.success(`Filed ${filed} ${filed === 1 ? 'file' : 'files'} against ${trackingNumber}.`);
    }

    if (failed.length === 0) {
      onSaved();
      return;
    }

    // Keep only what failed on the form, with its reason, so the reader can see what to retry.
    setFailures(failed);
    setFiles(files.filter((f) => failed.some((x) => x.name === f.name)));
    setConfirmed(false);

    if (filed > 0) onSaved();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Attach files to ${trackingNumber}`}
      size="lg"
      id="routing-attach-modal"
      confirm={{ id: 'routing-attach-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="routing-attach-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="routing-attach-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={`Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {failures.length > 0 ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <ul className="flex flex-col gap-1 text-xs text-red-700">
              {failures.map((f) => (
                <li key={f.name}>
                  {f.name} — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <RoutingFilesInput id="routing-attach-files" files={files} onChange={setFiles} disabled={saving} />
      </div>
    </Modal>
  );
}
